import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  HStack,
  Stack,
  Button,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import {
  getLibrariesData,
  updateLibrariesData,
} from "../../../services/Libraries";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import ImageWidget from "../../../CommonComponents/ImageWidget";
import { reverseTransformData, transformData } from "../../../util/helper";
import ImagePreview from "../../../components/ImagePreview";
import FormLoader from "../../../CommonComponents/FormLoader";
import { useSelector } from "react-redux";
import { handleApiError, showSuccessToast } from "../../../util/errorHandler";

// Renders nothing; keeps the field value in formData (ui:widget "hidden" does not apply to array fields)
const HiddenField = () => null;

const Section2 = () => {
  const { alias } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const previewEnabled = useSelector((state) => state.preview.isEnabled);
  const toast = useToast();
  const dataWithImage = (data) => ({
    ...data,
    preview_content_image: data?.content_image,
    preview_direct_url_of_preview_image: data?.direct_url_of_preview_image,
    preview_direct_url_of_content_image: data?.direct_url_of_content_image,
    preview_index_image: data?.index_image_aias,
  });

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = [
      "_id",
      "bibliographic_caption",
      "bibliographic_citation",
      "content_image",
      "copyright_of_image",
      "preview_content_image",
      "direct_url_of_preview_image",
      "preview_direct_url_of_preview_image",
      "direct_url_of_content_image",
      "preview_direct_url_of_content_image",
      "index_image_aias",
      "preview_index_image",
      "scanned_pages_image_alias",
      "featured_artist_facial_recognition_names",
      "full_name_alias",
      "full_name_joint_creator_alias",
    ];
    const rightFields = [
      "creator_name",
      "joint_creator_name",
      "content_image_thumbnails",
      "content_image_names",
      "direct_url_of_content_images",
      "canto_content_ids",
    ];

    const leftData = {};
    const rightData = {};

    leftFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      }
    });

    rightFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      }
    });

    return { leftData, rightData };
  };

  // Combine data from two forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Form change handlers
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Left column schema
  const leftColumnSchema = {
    title: "",
    type: "object",
    properties: {
      _id: { type: "string" },
      bibliographic_caption: {
        type: "string",
        title: "Bibliographic caption",
        readOnly: true,
      },
      bibliographic_citation: {
        type: "string",
        title: "Bibliographic citation",
        readOnly: true,
      },
      content_image: {
        type: "string",
        title: "Content image",
      },
      copyright_of_image: {
        type: "array",
        items: { type: "string" },
        title: "Copyright of image",
      },
      preview_content_image: {
        type: "string",
        title: "Image URL",
      },
      direct_url_of_preview_image: {
        type: "string",
        title: "Direct URL of preview image",
      },
      preview_direct_url_of_preview_image: {
        type: "string",
        title: "Image URL",
      },
      direct_url_of_content_image: {
        type: "string",
        title: "Direct URL of content image",
      },
      preview_direct_url_of_content_image: {
        type: "string",
        title: "Image URL",
      },
      index_image_aias: {
        type: "string",
        title: "Index image alias",
      },
      preview_index_image: {
        type: "string",
        title: "Image URL",
      },
      scanned_pages_image_alias: {
        type: "array",
        title: "Scanned pages image alias",
        items: {
          type: "object",
          properties: {
            image_alias: {
              type: "string",
              title: "Image Alias",
              default: "",
            },
            image: {
              type: "string",
              title: "Image",
              default: "",
            },
          },
        },
      },
      featured_artist_facial_recognition_names: {
        type: "string",
        title: "Featured artist facial recognition names",
      },
      full_name_alias: {
        type: "string",
        title: "Full name alias",
      },
      full_name_joint_creator_alias: {
        type: "string",
        title: "Full name joint creator alias",
      },
    },
  };

  // Right column schema
  const rightColumnSchema = {
    title: "",
    type: "object",
    properties: {
      creator_name: {
        type: "string",
        title: "Creator name",
      },
      joint_creator_name: {
        type: "string",
        title: "Joint creator name",
      },
      content_image_thumbnails: {
        type: "array",
        title: "Content image thumbnails",
        items: {
          type: "object",
          properties: {
            image_alias: {
              type: "string",
              title: "Image Alias",
              default: "",
            },
            image: {
              type: "string",
              title: "Image",
              default: "",
            },
          },
        },
      },
      content_image_names: {
        type: "array",
        items: { type: "string" },
        title: "Content image names",
      },
      direct_url_of_content_images: {
        type: "array",
        title: "Direct url of content images",
        items: {
          type: "object",
          properties: {
            image_alias: {
              type: "string",
              title: "Image Alias",
              default: "",
            },
            image: {
              type: "string",
              title: "Image",
              default: "",
            },
          },
        },
      },
      canto_content_ids: {
        type: "array",
        items: { type: "string" },
        title: "Canto content IDs",
      },
    },
  };

  // Combined schema for preview mode
  const combinedSchema = {
    title: "Quadrant-2 fields",
    description: "",
    type: "object",
    properties: {
      _id: { type: "string" },
      bibliographic_caption: {
        type: "string",
        title: "Bibliographic caption",
        readOnly: true,
      },
      bibliographic_citation: {
        type: "string",
        title: "Bibliographic citation",
        readOnly: true,
      },
      content_image: {
        type: "string",
        title: "Content image",
      },
      copyright_of_image: {
        type: "array",
        items: { type: "string" },
        title: "Copyright of image",
      },
      preview_content_image: {
        type: "string",
        title: "Image URL",
      },
      direct_url_of_preview_image: {
        type: "string",
        title: "Direct URL of preview image",
      },
      preview_direct_url_of_preview_image: {
        type: "string",
        title: "Image URL",
      },
      direct_url_of_content_image: {
        type: "string",
        title: "Direct URL of content image",
      },
      preview_direct_url_of_content_image: {
        type: "string",
        title: "Image URL",
      },
      index_image_aias: {
        type: "string",
        title: "Index image alias",
      },
      preview_index_image: {
        type: "string",
        title: "Image URL",
      },
      scanned_pages_image_alias: {
        type: "array",
        title: "Scanned pages image alias",
        items: {
          type: "object",
          properties: {
            image_alias: {
              type: "string",
              title: "Image Alias",
              default: "",
            },
            image: {
              type: "string",
              title: "Image",
              default: "",
            },
          },
        },
      },
      featured_artist_facial_recognition_names: {
        type: "string",
        title: "Featured artist facial recognition names",
      },
      full_name_alias: {
        type: "string",
        title: "Full name alias",
      },
      full_name_joint_creator_alias: {
        type: "string",
        title: "Full name joint creator alias",
      },
      creator_name: {
        type: "string",
        title: "Creator name",
      },
      joint_creator_name: {
        type: "string",
        title: "Joint creator name",
      },
      content_image_thumbnails: {
        type: "array",
        title: "Content image thumbnails",
        items: {
          type: "object",
          properties: {
            image_alias: {
              type: "string",
              title: "Image Alias",
              default: "",
            },
            image: {
              type: "string",
              title: "Image",
              default: "",
            },
          },
        },
      },
      content_image_names: {
        type: "array",
        items: { type: "string" },
        title: "Content image names",
      },
      direct_url_of_content_images: {
        type: "array",
        title: "Direct url of content images",
        items: {
          type: "object",
          properties: {
            image_alias: {
              type: "string",
              title: "Image Alias",
              default: "",
            },
            image: {
              type: "string",
              title: "Image",
              default: "",
            },
          },
        },
      },
      canto_content_ids: {
        type: "array",
        items: { type: "string" },
        title: "Canto content IDs",
      },
    },
  };

  // Left column UI schema
  const leftColumnUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    _id: { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    preview_content_image: {
      "ui:widget": ImageWidget,
    },
    preview_direct_url_of_preview_image: {
      "ui:widget": ImageWidget,
    },
    preview_direct_url_of_content_image: {
      "ui:widget": ImageWidget,
    },
    preview_index_image: {
      "ui:widget": ImageWidget,
    },
    scanned_pages_image_alias: {
      items: {
        image: {
          "ui:widget": ImageWidget,
        },
      },
    },
    bibliographic_caption: { "ui:emptyValue": "" },
    bibliographic_citation: { "ui:emptyValue": "" },
    content_image: { "ui:emptyValue": "" },
    copyright_of_image: { "ui:emptyValue": "" },
    direct_url_of_preview_image: { "ui:emptyValue": "" },
    direct_url_of_content_image: { "ui:emptyValue": "" },
    index_image_aias: { "ui:emptyValue": "" },
    featured_artist_facial_recognition_names: { "ui:widget": "hidden" },
    full_name_alias: { "ui:emptyValue": "" },
    full_name_joint_creator_alias: { "ui:emptyValue": "" },
  };

  // Right column UI schema
  const rightColumnUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    "ui:style": {
      fontSize: "14px",
    },
    direct_url_of_content_images: {
      items: {
        image: {
          "ui:widget": ImageWidget,
        },
      },
    },
    content_image_thumbnails: {
      items: {
        image: {
          "ui:widget": ImageWidget,
        },
      },
    },
    creator_name: { "ui:emptyValue": "" },
    joint_creator_name: { "ui:emptyValue": "" },
    content_image_names: { "ui:emptyValue": "" },
    canto_content_ids: { "ui:field": HiddenField },
  };

  // Combined UI schema for preview mode
  const combinedUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    _id: { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    preview_content_image: {
      "ui:widget": ImageWidget,
    },
    preview_direct_url_of_preview_image: {
      "ui:widget": ImageWidget,
    },
    preview_direct_url_of_content_image: {
      "ui:widget": ImageWidget,
    },
    preview_index_image: {
      "ui:widget": ImageWidget,
    },
    direct_url_of_content_images: {
      items: {
        image: {
          "ui:widget": ImageWidget,
        },
      },
    },
    content_image_thumbnails: {
      items: {
        image: {
          "ui:widget": ImageWidget,
        },
      },
    },
    scanned_pages_image_alias: {
      items: {
        image: {
          "ui:widget": ImageWidget,
        },
      },
    },
    bibliographic_caption: { "ui:emptyValue": "" },
    bibliographic_citation: { "ui:emptyValue": "" },
    content_image: { "ui:emptyValue": "" },
    copyright_of_image: { "ui:emptyValue": "" },
    direct_url_of_preview_image: { "ui:emptyValue": "" },
    direct_url_of_content_image: { "ui:emptyValue": "" },
    index_image_aias: { "ui:emptyValue": "" },
    featured_artist_facial_recognition_names: { "ui:widget": "hidden" },
    full_name_alias: { "ui:emptyValue": "" },
    full_name_joint_creator_alias: { "ui:emptyValue": "" },
    creator_name: { "ui:emptyValue": "" },
    joint_creator_name: { "ui:emptyValue": "" },
    content_image_names: { "ui:emptyValue": "" },
    canto_content_ids: { "ui:field": HiddenField },
  };

  const type = window.location.pathname.split("/")[1];
  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getLibrariesData(alias, "section-2", type);
          if (result) {
            const fieldsToTransform = [
              "direct_url_of_content_images",
              "content_image_thumbnails",
              "scanned_pages_image_alias",
            ];

            // To Show image on form need to provide url to image field which is only used to show the image
            const updatedDataWithImage = dataWithImage(result?.data?.[0]);
            const transformedData = transformData(
              updatedDataWithImage,
              fieldsToTransform,
            );

            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(transformedData);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(transformedData);
            setQuickSave(false);
          }
        } else {
          // For new records, initialize empty forms
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data",
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      let finalFormData;

      if (previewEnabled) {
        finalFormData = combinedFormData;
      } else {
        finalFormData = combineFormData(leftFormData, rightFormData);
      }

      // Remove _id from create operations
      if (!alias && finalFormData._id) {
        delete finalFormData._id;
      }

      const fieldsToTransform = [
        "content_image_thumbnails",
        "direct_url_of_content_images",
        "scanned_pages_image_alias",
      ];

      const updatedFormData = reverseTransformData(
        finalFormData,
        fieldsToTransform,
      );

      if (alias) {
        const result = await updateLibrariesData(
          alias,
          updatedFormData,
          "",
          "section-2",
        );

        if (result?.status === 200) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to update data",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Box
                flex={1}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                p={4}
                borderRadius="md"
              >
                <HStack justifyContent="space-between" mb={4}>
                  <Box fontSize="lg" fontWeight="bold">
                    Library Quadrant-2 Fields
                  </Box>
                  <IconButton
                    aria-label={
                      showImagePreview
                        ? "Hide image preview"
                        : "Show image preview"
                    }
                    icon={showImagePreview ? <ViewOffIcon /> : <ViewIcon />}
                    size="sm"
                    colorScheme={showImagePreview ? "red" : "blue"}
                    onClick={() => setShowImagePreview((prev) => !prev)}
                    title={
                      showImagePreview
                        ? "Close image preview"
                        : "Open image preview"
                    }
                  />
                </HStack>
                <Form
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  fontsize={"14px"}
                />
              </Box>
              {/* Third column for image preview when clicked */}
              {showImagePreview && (
                <HStack
                  sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}
                >
                  <ImagePreview
                    image={
                      combinedFormData?.direct_url_of_preview_image ||
                      combinedFormData?.preview_direct_url_of_preview_image ||
                      combinedFormData?.content_image ||
                      combinedFormData?.preview_content_image ||
                      combinedFormData?.direct_url_of_content_image ||
                      combinedFormData?.preview_direct_url_of_content_image
                    }
                  />
                </HStack>
              )}
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Stack
                flexDirection="row"
                gap={4}
                alignItems="flex-start"
                flex={1}
              >
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <HStack justifyContent="space-between" mb={4}>
                      <Box fontSize="lg" fontWeight="bold">
                        Library Quadrant-2 Fields
                      </Box>
                      <IconButton
                        aria-label={
                          showImagePreview
                            ? "Hide image preview"
                            : "Show image preview"
                        }
                        icon={showImagePreview ? <ViewOffIcon /> : <ViewIcon />}
                        size="sm"
                        colorScheme={showImagePreview ? "red" : "blue"}
                        onClick={() => setShowImagePreview((prev) => !prev)}
                        title={
                          showImagePreview
                            ? "Close image preview"
                            : "Open image preview"
                        }
                      />
                    </HStack>
                    <Form
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Form
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
              {/* Third column for image preview when clicked */}
              {showImagePreview && (
                <HStack
                  sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}
                >
                  <ImagePreview
                    image={
                      leftFormData?.direct_url_of_preview_image ||
                      leftFormData?.preview_direct_url_of_preview_image ||
                      leftFormData?.content_image ||
                      leftFormData?.preview_content_image ||
                      leftFormData?.direct_url_of_content_image ||
                      leftFormData?.preview_direct_url_of_content_image
                    }
                  />
                </HStack>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Fixed Save Button */}
      <Box
        position="fixed"
        bottom={0}
        left={"250px"}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
      >
        <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            width="auto"
            margin="0 auto"
            textAlign="center"
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Section2;
